import {
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Form } from 'antd';
import moment from 'moment';
import useRidesCarriagesInfo from '../../hooks/queryHooks/useRidesCarriagesInfo';
import useSavedPassengers from '../../hooks/queryHooks/useSavedPassengers';
import useQuery from '../../hooks/useQuery';
import useChangePaymentVolume from '../../hooks/queryHooks/useChangePaymentVolume';
import useLoginOptions from '../../hooks/queryHooks/useLoginOptions';
import useTranslation from '../../hooks/translation/useTranslation';
import useCustomerPaymentCards from '../../hooks/queryHooks/useCustomerPaymentCards';
import { cancelStatement } from '../../../services/customer/ticketBookingServices';
import TicketCancelLogic from './components/TicketCancelLogic';

const TicketBookingContext = createContext({
  departureChosenSeats: [],
  setDepartureChosenSeats: () => {},
  returnChosenSeats: [],
  setReturnChosenSeats: () => {},
  statement: {},
  setStatement: () => {},
  savedPassengers: [],
  services: [],
  setServices: () => {},
  departurePassengersForm: () => {},
  returnPassengersForm: () => {},
  departureClassesForm: () => {},
  returnClassesForm: () => {},
  bookingInfoForm: () => {},
  paymentMethodForm: () => {},
  activeStep: 1,
  setActiveStep: () => {},
  bookingData: {},
  ridesCarriagesInfo: {},
  steps: [],
  options: {},
  paymentCards: [],
  prices: {
    seatClassPrices: {},
    seatGroupPrices: {},
  },
  setPrices: () => {},
  cancelRef: {
    current: 'cancel',
  },
  isNextDisabled: false,
  setIsNextDisabled: () => {},
  isDesktop: true,
  setIsDesktop: () => {},
});

export const useBooking = () => useContext(TicketBookingContext);
export function TicketBookingProvider({ children, bookingData }) {
  const { trans, currentLanguage } = useTranslation();
  const query = useQuery();
  const seatFormation = query.get('seatFormation');
  const cancelRef = useRef();

  const [departureChosenSeats, setDepartureChosenSeats] = useState([]);
  const [returnChosenSeats, setReturnChosenSeats] = useState([]);
  const [statement, setStatement] = useState({
    expirationDate: seatFormation === 'select' ? moment().add(10, 'minutes') : null,
  });
  const [activeStep, setActiveStep] = useState(1);
  const [services, setServices] = useState([]);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [prices, setPrices] = useState({
    seatClassPrices: {},
    seatGroupPrices: {},
  });
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  // Query Hooks
  const savedPassengers = useSavedPassengers({ seatFormation });
  const { options } = useLoginOptions({ seatFormation });
  const ridesCarriagesInfo = useRidesCarriagesInfo({ bookingData, seatFormation });
  useChangePaymentVolume({ query });

  const [departurePassengersForm] = Form.useForm();
  const [returnPassengersForm] = Form.useForm();
  const [departureClassesForm] = Form.useForm();
  const [returnClassesForm] = Form.useForm();
  const [bookingInfoForm] = Form.useForm();
  const [paymentMethodForm] = Form.useForm();

  const { paymentCards, fetchCards } = useCustomerPaymentCards();

  useEffect(() => {
    fetchCards();
  }, []);

  const getSteps = () => {
    const steps = [];
    if (seatFormation === 'book') {
      steps.push(trans('statement_info'));
      steps.push(trans('departure_classes'));
      if (bookingData?.return?.id) {
        steps.push(trans('arrival_classes'));
      }
      steps.push(trans('confirm'));
    } else {
      if (seatFormation === 'select') {
        steps.push(trans('departure_classes'));
        if (bookingData?.return?.id) {
          steps.push(trans('arrival_classes'));
        }
      }
      if (seatFormation === 'auto' || seatFormation === 'booking') {
        steps.push(trans('departure_classes'));
        if (bookingData?.return?.id) {
          steps.push(trans('arrival_classes'));
        }
      }
      steps.push(trans('departure_passengers'));
      if (bookingData?.return?.id || bookingData?.return?.rideId) {
        steps.push(trans('arrival_passengers'));
      }

      if (seatFormation === 'booking') {
        steps.push(trans('confirm'));
      } else if (bookingData?.invoice) {
        steps.push(trans('invoice_terms'));
      } else {
        steps.push(trans('pay'));
      }
    }

    return steps;
  };

  const contextValue = useMemo(() => ({
    departureChosenSeats,
    setDepartureChosenSeats,
    returnChosenSeats,
    setReturnChosenSeats,
    statement,
    setStatement,
    savedPassengers,
    services,
    setServices,
    departurePassengersForm,
    returnPassengersForm,
    activeStep,
    setActiveStep,
    ridesCarriagesInfo,
    bookingData,
    isDesktop,
    steps: getSteps(),
    departureClassesForm,
    returnClassesForm,
    bookingInfoForm,
    options,
    paymentCards,
    paymentMethodForm,
    prices,
    setPrices,
    cancelRef,
    isNextDisabled,
    setIsNextDisabled,
    setIsDesktop,
  }), [
    departureChosenSeats,
    returnChosenSeats,
    statement,
    savedPassengers,
    services,
    activeStep,
    ridesCarriagesInfo,
    options,
    currentLanguage,
    paymentCards,
    prices,
    isNextDisabled,
    isDesktop,
  ]);

  return (
    <TicketBookingContext.Provider value={contextValue}>
      <TicketCancelLogic
        cancelRef={cancelRef}
        statement={statement}
        departureChosenSeats={departureChosenSeats}
        returnChosenSeats={returnChosenSeats}
        bookingData={bookingData}
        ridesCarriagesInfo={ridesCarriagesInfo}
        cancelStatement={cancelStatement}
      />
      {children}
    </TicketBookingContext.Provider>
  );
}
